import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import {
    Thread,
    ThreadDialogService,
    ThreadInputAreaComponent,
    ThreadMessageComponent,
    ThreadMessageUnderModeration,
    ThreadService,
    Threads,
} from "@teammood/timeline";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { catchError, EMPTY, filter, Subject, switchMap, takeUntil, tap } from "rxjs";
import { Task } from "../../taskbook.types";
import { UserInfos } from "../../../shared/ui/username-icon/user-infos.type";
import { LoggerService } from "@teammood/shared/util/logger";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { CommonModule } from "@angular/common";
import { TaskThreadReactionsComponent } from "./task-thread-reactions/task-thread-reactions.component";


@Component({
    selector: "tm-task-threads",
    templateUrl: "./task-threads.component.html",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        TranslateModule,
        ThreadMessageUnderModeration,
        ThreadMessageComponent,
        ThreadInputAreaComponent,
        TaskThreadReactionsComponent
    ]
})
export class TaskThreadsComponent implements OnChanges, OnDestroy {

    @Input() task: Task & Threads;
    @Input() userInfos: UserInfos;
    @Input() threadInCreation: boolean;
    
    @Output() onThreadCreation = new EventEmitter()
    @Output() onCancelCreation = new EventEmitter()
    @Output() onThreadFocus = new EventEmitter()

    protected threads: Thread[];
    protected newThread: Thread;
    protected threadsLimitTo: number = -2;

    private _destroy$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private translate: TranslateService,
        private threadService: ThreadService,
        private snackBarService: SnackBarService,
        private threadDialogService: ThreadDialogService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if(changes.task?.currentValue){
            this.threads = this.task.threads;
            this.newThread = new Thread(false);
        }
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    showMore(){
        this.threadsLimitTo = this.threadsLimitTo - 10;
    }

    protected onThreadAdded(newThread: Thread){
        if (!newThread.message.isEmpty()) {
            this.onThreadCreation.emit(newThread);
            this.threadInCreation = false; 
        }
    }

    onThreadReactionsUpdated(updatedTaskItem: Threads){
        this.threads = updatedTaskItem.threads;
    }

    protected async deleteThread(thread: Thread){
        const dialogRef = await this.threadDialogService.delete();

        dialogRef.beforeClosed().pipe(
            filter(shouldDelete => !!shouldDelete),
            switchMap((_) => {
                return this.threadService.deleteThread(this.task.id, this.task.itemType, thread.id).pipe(
                    tap(updatedTimelineItem => {
                        this.snackBarService.success(this.translate.instant('thread_deleted'))
                        this.threads = updatedTimelineItem.threads;
                    })
                );
            }),
            catchError(error => {
                this.logger.error('Error deleting thread:', error);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    protected async moderateThread(thread: Thread){
        const dialogRef = await this.threadDialogService.moderate();

        dialogRef.beforeClosed().pipe(
            filter(why => !!why),
            switchMap((why) => {
                return this.threadService.moderateThread(this.task.id, this.task.itemType, thread.id, why).pipe(
                    tap(updatedTimelineItem => {
                        this.snackBarService.success(this.translate.instant('moderation_success'))
                        this.threads = updatedTimelineItem.threads;
                    })
                );
            }),
            catchError(error => {
                this.logger.error('Error moderating thread:', error);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
            takeUntil(this._destroy$)
          ).subscribe();
    }

    protected  onEditedThread(thread: Thread) {
        this.threadService.updateThread(this.task.id, this.task.itemType, thread).pipe(
            tap(updatedTimelineItem => {
                this.snackBarService.success(this.translate.instant('thread_updated'));
                this.threads = updatedTimelineItem.threads;
            }),
            catchError(error => {
                this.logger.error('Error updating thread:', error);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
        ).subscribe();
    }

    protected onCancel() {
        this.onCancelCreation.emit(null);
        this.threadInCreation = false; 
    }

}